import React, { useEffect, useState, useRef } from "react";
import PipelineCard from "./PipelineCard";
import { getDatasetDescription } from "./datasetDescriptions";
import "./style.css";
import {
  BiFilter,
  BiSearch,
  BiGridAlt,
  BiListUl,
  BiCalendar,
  BiLinkExternal,
} from "react-icons/bi";
import { AllResults, S3UrlHead } from "services";
import useLocalStorage from "../../hooks/useLocalstorage";
import { useSearchParams, useNavigate } from "react-router-dom";

const Index = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { value: viewMode, setValue: setViewMode } = useLocalStorage(
    "dashboard-view-mode",
    "card"
  );

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);

  // Get filter directly from URL params, default to "all"
  const pipelineFilter = searchParams.get("type") || "all";

  // Handle filter changes by updating URL
  const handleFilterChange = (newFilter) => {
    const params = new URLSearchParams(searchParams);

    if (newFilter === "all") {
      params.delete("type");
    } else {
      params.set("type", newFilter);
    }

    navigate(`?${params.toString()}`, { replace: true });
  };

  // Data fetching with abort controller to prevent race conditions
  useEffect(() => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    setLoading(true);

    AllResults()
      .then((res) => {
        // Only update state if request wasn't aborted
        if (!currentController.signal.aborted) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((error) => {
        // Only handle error if not aborted
        if (!currentController.signal.aborted) {
          console.error("Failed to fetch data:", error);
          setLoading(false);
        }
      });

    // Cleanup function
    return () => {
      if (currentController) {
        currentController.abort();
      }
    };
  }, []);

  const uniquePipelines = Array.from(
    new Set(data.map((run) => run.pipeline))
  ).sort();

  const filteredRuns = data.filter((run) => {
    const matchesSearch =
      run.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      run.pipeline.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPipeline =
      pipelineFilter === "all" || run.pipeline === pipelineFilter;
    return matchesSearch && matchesPipeline;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const handleViewResults = (path, dataset, isNestedPath) => {
    const topLevelDomain = window.location.origin;
    // Create base path based on pipeline type
    const basePath = path === "imagepipeline" ? "image" : path;

    // Create the S3 URL and properly encode it
    const s3UrlPath = isNestedPath ? `${path}/${path}/${dataset}` : `${path}/${dataset}`;
    const s3Url = new URL(`https://storage.googleapis.com/aiensured-static-report/ai.datasets/lab/${s3UrlPath}`);
    const encodedS3Url = encodeURIComponent(s3Url.toString());

    // Construct the final results URL
    const resultsUrl = `${topLevelDomain}/${basePath}/params?s3Url=${encodedS3Url}`;
    window.open(resultsUrl, "_blank");
    console.log(`Viewing results for pipeline: ${resultsUrl}`);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">AiEnsured Pipeline Dashboard</h1>
            <p className="dashboard-subtitle">Loading pipelines...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="dashboard-logo">
            <img className="" src="/ai.png" alt="" />
            <h3>AiEnsured</h3>
          </div>

          <h1 className="dashboard-title"> Pipeline Dashboard</h1>
          <p className="dashboard-subtitle">
            List of your AI/ML testing pipelines
          </p>
        </div>
      </div>

      <div className="dashboard-controls">
        <div className="search-container">
          <BiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search pipelines..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-container">
          <BiFilter className="filter-icon" />
          <select
            className="filter-select"
            value={pipelineFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
          >
            <option value="all">All Pipelines</option>
            {uniquePipelines.map((pipeline) => (
              <option key={pipeline} value={pipeline}>
                {pipeline}
              </option>
            ))}
          </select>
        </div>

        <div className="view-toggle">
          <button
            className={`view-button ${viewMode === "card" ? "active" : ""}`}
            onClick={() => setViewMode("card")}
            title="Card View"
          >
            <BiGridAlt />
          </button>
          <button
            className={`view-button ${viewMode === "table" ? "active" : ""}`}
            onClick={() => setViewMode("table")}
            title="Table View"
          >
            <BiListUl />
          </button>
        </div>
      </div>

      {viewMode === "card" ? (
        <div className="pipeline-grid">
          {filteredRuns.map((run) => (
            <PipelineCard
              key={run.id}
              pipelineRun={run}
              clickHandler={() => handleViewResults(run.path, run.dataset, run.isNestedPath)}
            />
          ))}
        </div>
      ) : (
        <div className="pipeline-table-container">
          <table className="pipeline-table">
            <thead>
              <tr>
                <th>Pipeline Name</th>
                <th>Pipeline Type</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRuns.map((run) => {
                const { date, time } = formatTimestamp(run.timestamp);
                return (
                  <tr key={run.id}>
                    <td className="pipeline-name">
                      <div className="table-dataset-name">{run.name}</div>
                      <div className="table-dataset-desc">{getDatasetDescription(run.dataset, run.pipeline)}</div>
                    </td>
                    <td>
                      <span className="pipeline-type-badge">
                        {run.pipeline}
                      </span>
                    </td>

                    <td className="pipeline-time">
                      {date}at {time}
                    </td>
                    <td>
                      <button
                        className="table-results-button"
                        onClick={() => handleViewResults(run.path, run.dataset, run.isNestedPath)}
                      >
                        <BiLinkExternal className="button-icon" />
                        View Results
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredRuns.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            <BiSearch />
          </div>
          <h3>No pipelines found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default Index;
